<template>
  <div
    class="fixed md:bottom-6 right-0 max-md:left-0 mx-2 md:mx-6 z-50 space-y-2"
    :class="{
      'bottom-20 m-1': isAuthed,
      'bottom-4': !isAuthed
    }"
    >
    <span class="absolute -top-12 right-0">
      <UiButton
        :svg="modelPopUp ? 'fa fa-arrow-down' : 'fa fa-headset md:text-xl'"
        class="!rounded-full dark !aspect-square !shadow !shadow-gray-200"
        @click="modelPopUp = !modelPopUp"/>

      <div
        v-if="unreadCount"
        class="absolute -right-1 -top-1 bg-red-500 aspect-square h-5 rounded-full text-white text-center text-sm"
        v-text="unreadCount"
        />
    </span>

    <div v-show="modelPopUp" class="rounded-2xl">
      <div
        class="ui-card ml-auto md:w-[500px] max-w-full !p-0 !flex !flex-col !flex-grow relative !space-y-3 !shadow-xl !shadow-gray-400 max-h-[70vh] overflow-auto"
        >
        <UniqueSupportChat
          v-if="modelSession"
          ref="refChatWindow"
          v-model:session="modelSession"
          v-model:popup="modelPopUp"
          v-model:unread="unreadCount"
          @new-message="methodNewMessageReceived"
          @finished="methodChatFinished()"
          />

        <UniqueSupportSelect
          v-show="!modelSession"
          ref="refSelectWindow"
          v-model:popup="modelPopUp"
          v-model:session="modelSession"
          v-model:unread="unreadCount"
          />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const refSelectWindow = ref<InstanceType<typeof import('./Select.vue').default> | null>(null)
const refChatWindow = ref<InstanceType<typeof import('./Chat.vue').default> | null>(null)

const modelPopUp = ref<boolean>(false)
const modelSession = ref<string|null>(null)
const unreadCount = ref<number>(0)
const lastSoundPlayed = ref<number|null>(null)
const dataIsSessionFinished = ref<boolean>(true)

onMounted(() => {

  // === Listen for new support requests
  ws.listenWhileMounted<{
    message: string,
    type: string
  }>('info:support_request', null, (data: any) => {
    if (!modelPopUp.value){
      notify(data.message)
    }

    methodSetSession(data.session);

    modelPopUp.value = true

    methodNewMessageReceived()
  })

  // === Listen for new support messages
  ws.listenWhileMounted<{
    message: string,
    type: string
  }>('info:new_support_messages', null, (data: any) => {
    if(data?.unread_count && data?.unread_count != unreadCount.value){
      methodNewMessageReceived()
    }

    unreadCount.value = data.unread_count ?? 0

    if ((user.value as User)?.is_admin ?? false){
      refSelectWindow.value?.methodShowOpenSessions()
    }
  })

  // === Load session from local storage
  if (localStorage.getItem('support_session')){
    modelSession.value = localStorage.getItem('support_session')
    modelPopUp.value = true
  }

  // === Set session
  ws.listenWhileMounted<{
    session: string,
    openClosed: boolean
  }>('info:set_session', null, async (data: any) => {
    if(!modelPopUp.value && data.openClosed){
      modelPopUp.value = data.openClosed;
    }
    await methodNewMessageReceived()
    unreadCount.value += 1
    await nextTick()
    methodSetSession(data.session);
  })
})

const methodNewMessageReceived = () => {
  if(
    (lastSoundPlayed.value && (Date.now() - lastSoundPlayed.value) < 1000)
    || !modelSession.value
    || !modelPopUp.value
  ){
    return
  }

  lastSoundPlayed.value = Date.now()

  new Audio(api.getBackendUrl()+'warehouse/newmessage.mp3?clientasset=true').play().catch(() => {})

  if (!((user.value as User)?.is_admin ?? false) && !modelPopUp.value){
    unreadCount.value = unreadCount.value + 1
  }
}

const methodChatFinished = () => {
  localStorage.removeItem('support_session')
  dataIsSessionFinished.value = true
  unreadCount.value = 0
}

const methodSetSession = (newsession: string|null) => {
  if(!dataIsSessionFinished.value) {
    return
  }

  if (modelSession.value){
    modelSession.value = null

    return setTimeout(() => {
      if(modelSession.value)
        return

      modelSession.value = newsession
    }, 100)
  }

  modelSession.value = newsession
}

watch(modelSession, (session: string|null) => {
  if(session){
    localStorage.setItem('support_session', session)
    dataIsSessionFinished.value = false
  }else{
    localStorage.removeItem('support_session')
  }
})

watch(modelPopUp, (isVisible: boolean) => {
  if(isVisible){
    unreadCount.value = 0
    refChatWindow.value?.methodScrollDown()
  }
})
</script>
